import React from "react";
import * as R from "ramda";
import { normalize, formatDatetimeString } from "../formatters";

export { withApolloState } from "./apollo";
export { getSourceByPath } from "./source-utils";

export const xlsxType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

export const getValueFromPath = (
    path,
    data,
    separator = ".",
    defaultValue = ""
) => {
    const get = R.pipe(R.split(separator), R.pathOr(defaultValue, R.__, data));

    return get(path);
};

/**
 * Sets deep inside an object
 * with a given path
 */
export const setObj = (obj, path, value) => {
    const pathAsArray = ensureString(path).split(".");
    const target = R.lensPath(pathAsArray);
    return R.set(target, value, obj);
};

export const getJsonSchemaFromPath = (path, jsonSchema, separator = ".") => {
    if (R.or(R.isEmpty(jsonSchema), R.isNil(jsonSchema))) {
        return {};
    }

    const traverse = (obj, attrName) => {
        const isNumber = /^\d+$/;
        if (R.test(isNumber, attrName)) {
            return obj;
        } else if (R.equals(obj.type, "object")) {
            return (
                R.path(["properties", attrName], obj) ||
                R.path(["enum_types", attrName], obj) ||
                {}
            );
        } else if (R.equals(obj.type, "array")) {
            return R.pathOr({}, ["items", "properties", attrName], obj);
        } else {
            return {};
        }
    };

    return R.reduce(
        traverse,
        jsonSchema,
        R.split(separator, ensureString(path))
    );
};

/**
 * Takes an object path ie 'a.b.c'
 * and searches for the title
 * in the JSON schema for that object
 * returning key as a fallback
 * @param {String} path
 * @param {Object} schema - json schema
 * @return {String}
 */
export const getTitleFromPath = (path, schema) => {
    const localSchema = getJsonSchemaFromPath(path, schema);
    return localSchema.title || normalize(R.last(path.split(".")));
};

// Takes an object along with a json schema, replaces each key with
// the correct title.
export const fmtTitles = (obj, schema, prefix) =>
    Object.keys(obj).reduce((acc, key) => {
        const title = getTitleFromPath(
            prefix ? `${prefix}.${key}` : key,
            schema
        );

        if (!obj[key]) return { ...acc, [title]: null };
        switch (obj[key].constructor) {
            case Object:
                return {
                    ...acc,
                    [title]: fmtTitles(obj[key], schema, key),
                };
            case Array:
                return {
                    ...acc,
                    [title]: obj[key].map(e => fmtTitles(e, key)),
                };
            default:
                return { ...acc, [title]: obj[key] };
        }
    }, {});

export const debounce = (func, wait, immediate) => {
    let timeout;

    return function() {
        const context = this;
        // eslint-disable-next-line no-undef
        const args = arguments;

        const later = () => {
            timeout = null;
            if (!immediate) func.apply(context, args);
        };

        const callNow = immediate && !timeout;

        clearTimeout(timeout);
        timeout = setTimeout(later, wait);

        if (callNow) func.apply(context, args);
    };
};

export const ensureString = v => "" + v;

/**
 * Recurse through object,
 * generating an array of paths for
 * each item in the object
 * @param value{Mixed} Source object/ value in recursive loop (required)
 * @param path{Array} array of current path location inside the object
 * @return {Array} path results
 */
export const deepExtractObjectPaths = (value, path = []) => {
    const itemType = R.type(value);
    if (["Object", "Array"].indexOf(itemType) === -1) {
        // prevent array mutation
        return [path.join(".")];
    }
    if (
        Array.isArray(value) &&
        value.length > 0 &&
        value[0].constructor !== Object
    ) {
        return [path.join(".")];
    }
    const getChildPath = (val, k) =>
        deepExtractObjectPaths(val, path.concat([k]));
    return R.flatten(mapIndexed(getChildPath, value));
};

/**
 * Equivalent of lodash `map` function that
 * calls `fn` with value, key/index on either an array
 * @param {Function} fn iterator function
 * @param {Mixed} val initial value
 */
export const mapIndexed = (fn, val) => {
    if (R.type(val) === "Object") {
        return R.values(R.mapObjIndexed(fn, val));
    }
    if (R.type(val) === "Array") {
        const mapIndexed = R.addIndex(R.map);
        return mapIndexed(fn, val);
    }
};

export const parseFilenameFromContentDisposition = cd => {
    if (cd.startsWith("=?utf-8?b?") && cd.endsWith("?=")) {
        cd = cd.substring(10, cd.length - 2);
        cd = b64DecodeUnicode(cd);
    }
    return cd.split("filename=")[1];
};

export const b64DecodeUnicode = str => {
    // Going backwards: from bytestream, to percent-encoding, to original string.
    return decodeURIComponent(
        atob(str)
            .split("")
            .map(function(c) {
                return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
            })
            .join("")
    );
};

export const withProps = (Component, props) => otherProps => (
    <Component {...props} {...otherProps} />
);

export const camelToRegular = str =>
    str
        .split(/(?=[A-Z])/)
        .map(p => p.charAt(0).toUpperCase() + p.slice(1))
        .join(" ");

export const stringifyTags = tagsObject => {
    return Object.entries(tagsObject).reduce(
        (acc, [key, value], index, array) => {
            const last = array.length === index + 1;
            const postfix = last ? "" : "&";
            return acc + key + ":" + value + postfix;
        },
        ""
    );
};

export const generateLinesOfBusiness = lobs =>
    lobs.map(el => {
        if (typeof el === "object") {
            const externalIdentifier =
                el.externalIdentifier &&
                ` with External ID ${el.externalIdentifier}`;
            return `${el.lineOfBusiness} terminated on ${formatDatetimeString({
                str: el.timestamp,
            })}${externalIdentifier}`;
        } else {
            return el;
        }
    });

export const renderLinesOfBusiness = lobs =>
    generateLinesOfBusiness(lobs).join(", ");

export const linesOfBusinessAccessor = (baseAccessor = "") => row => {
    // get linesOfBusiness array or empty array, then join with commas
    return renderLinesOfBusiness(
        R.pathOr(
            [],
            baseAccessor
                .split(".")
                .filter(Boolean)
                .concat(["linesOfBusiness"]),
            row
        )
    );
};

export const getCountrySource = entity => {
    const isOrg = entity.entityType === "ORGANISATION";
    const addressKeys = ["legal", "physical"];
    const isOwnAddressPresent = Boolean(
        R.pathOr(
            false,
            [`${addressKeys[Number(!isOrg)]}Address`, "countryCode"],
            entity
        )
    );
    // true^true === 0, true^false === 1
    return addressKeys[isOwnAddressPresent ^ isOrg];
};

/**
 * @description
 * Maps over all values of an object deeply, and applies the given function.
 * If you don't want to map deeply, consider `R.mapObjIndexed`.
 * @function
 * @param {Function}
 * @param {Object}
 * @returns {Object}
 */
export const mapObjDeep = R.curry((func, val) => {
    if (R.type(val) === "Array") {
        return R.map(mapObjDeep(func), val);
    } else if (R.type(val) === "Object") {
        return R.mapObjIndexed(mapObjDeep(func), val);
    }

    return func(val);
});

/**
 * @description
 * recursively applies some function to object and it's deep children with type "object"
 * @function
 * @param fn{Function} - function to apply to object
 * @returns {any}
 */
export const fnObjDeep = fn => obj => {
    if (R.is(Array, obj)) {
        return R.map(fnObjDeep(fn), obj);
    } else if (R.is(Object, obj)) {
        return R.mapObjIndexed(fnObjDeep(fn), fn(obj));
    } else {
        return obj;
    }
};

export const downloadFile = name => response =>
    response.blob().then(blob => {
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = name;
        link.click();
    });

export const timeout = time =>
    new Promise(resolve => {
        setTimeout(resolve, time);
    });

export const getFileFormat = file => {
    const match = file.name.match(/.*\.([a-zA-Z0-9]*)$/);
    return match ? match[1] : null;
};

export const transformFileContentType = (file, contentType) =>
    new File([file], file.name, { type: contentType });

export const validateBulkRequestFile = fileFormat =>
    ["csv", "xlsx"].includes(fileFormat);

export const transformBulkUploadFile = file => {
    const fileFormat = getFileFormat(file);
    if (!validateBulkRequestFile(fileFormat)) {
        return false;
    }
    const contentType = fileFormat === "csv" ? "text/csv" : xlsxType;
    return transformFileContentType(file, contentType);
};

export const decodeId = id => {
    const decoded = atob(id).match(/:(.*)/);
    return R.pathOr(false, [1], decoded);
};

export const removeTypenames = fnObjDeep(R.dissoc("__typename"));
